import { apiGet } from "./http";
import type { MemberGroupAssignmentItem } from "./memberMemberGroup";

/** 已通过列表筛选「未分组」会员时使用的哨兵 key */
export const MEMBER_GROUP_FILTER_NONE = "__none__";

export type MemberGroupRoleDefinition = {
  roleKey: string;
  name: string;
  enabled?: boolean;
  sortOrder?: number;
};

export type MemberGroupDefinition = {
  id: string;
  name: string;
  icon?: string | null;
  enabled?: boolean;
  sortOrder?: number;
  roles: MemberGroupRoleDefinition[];
};

type MemberGroupDefinitionDto = {
  groupKey: string;
  name: string;
  icon?: string;
  enabled?: boolean;
  sortOrder?: number;
  roles?: MemberGroupRoleDefinition[];
};

function dtoToMemberGroupDefinition(d: MemberGroupDefinitionDto): MemberGroupDefinition | null {
  const id = String(d?.groupKey || "").trim();
  const name = String(d?.name || "").trim();
  if (!id || !name) return null;
  const roles = (Array.isArray(d.roles) ? d.roles : [])
    .map((r) => ({
      roleKey: String(r?.roleKey || "").trim(),
      name: String(r?.name || "").trim(),
      enabled: r?.enabled !== false,
      sortOrder: Number(r?.sortOrder ?? 0) || 0,
    }))
    .filter((r) => r.roleKey && r.name);
  return {
    id,
    name,
    icon: d.icon ? String(d.icon) : null,
    enabled: d.enabled !== false,
    sortOrder: d.sortOrder,
    roles,
  };
}

function mapMemberGroupDefinitionDtos(rows: MemberGroupDefinitionDto[] | null | undefined): MemberGroupDefinition[] {
  return (Array.isArray(rows) ? rows : [])
    .map(dtoToMemberGroupDefinition)
    .filter((x): x is MemberGroupDefinition => x != null);
}

export function enabledMemberGroupDefinitions(items: MemberGroupDefinition[]): MemberGroupDefinition[] {
  return items.filter((g) => g.enabled !== false);
}

export function enabledRolesForGroup(group: MemberGroupDefinition): MemberGroupRoleDefinition[] {
  return (group.roles ?? []).filter((r) => r.enabled !== false && String(r.name || "").trim());
}

export function formatMemberGroupLabel(groupName?: string | null, roleName?: string | null): string {
  const g = String(groupName || "").trim();
  const r = String(roleName || "").trim();
  if (!g && !r) return "";
  if (!g) return r;
  if (!r) return g;
  return `${g} · ${r}`;
}

/** 从会员行解析分组列表（兼容旧版单分组字段） */
export function memberGroupsFromMemberRow(m: {
  memberGroups?: MemberGroupAssignmentItem[] | null;
  memberGroupKey?: string | null;
  memberGroupName?: string | null;
  memberGroupRoleKey?: string | null;
  memberGroupRoleName?: string | null;
}): MemberGroupAssignmentItem[] {
  const rows = (Array.isArray(m.memberGroups) ? m.memberGroups : []).filter((x) =>
    String(x?.memberGroupKey || "").trim(),
  );
  if (rows.length > 0) return rows;
  const key = String(m.memberGroupKey || "").trim();
  if (!key) return [];
  return [
    {
      memberGroupKey: key,
      memberGroupName: m.memberGroupName ?? null,
      memberGroupRoleKey: m.memberGroupRoleKey ?? null,
      memberGroupRoleName: m.memberGroupRoleName ?? null,
    },
  ];
}

export function formatMemberGroupsDisplay(
  items: MemberGroupAssignmentItem[],
  groupDefs?: MemberGroupDefinition[],
): string {
  return items
    .map((item) => {
      const key = String(item.memberGroupKey || "").trim();
      if (!key) return "";
      const def = groupDefs?.find((g) => String(g.id || "").trim() === key);
      const groupName = String(item.memberGroupName || def?.name || key).trim();
      const roleName = String(item.memberGroupRoleName || "").trim();
      return formatMemberGroupLabel(groupName, roleName) || key;
    })
    .filter(Boolean)
    .join("；");
}

/** 仅展示分组名称（不含组内职位） */
export function memberGroupNamesDisplay(m: {
  memberGroups?: MemberGroupAssignmentItem[] | null;
  memberGroupKey?: string | null;
  memberGroupName?: string | null;
}): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const g of memberGroupsFromMemberRow(m)) {
    const name = String(g.memberGroupName || g.memberGroupKey || "").trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names.join("；");
}

export async function fetchAllMemberGroupDefinitionsForTenant(tenantLegacyId: string): Promise<MemberGroupDefinition[]> {
  const tid = tenantLegacyId.trim();
  if (!tid) return [];
  const rows = await apiGet<MemberGroupDefinitionDto[]>(
    `/api/tenants/${encodeURIComponent(tid)}/member-group-definitions`,
  );
  return mapMemberGroupDefinitionDtos(rows);
}
